import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { fabric } from 'fabric';
import type { MessageDescriptor } from 'shared/i18n/i18n';
import { Group, Textbox } from 'fabric/fabric-impl';
import { CircleHelp } from 'lucide-react';
import ColorPicker from 'modules/template-editor/ui/components/elements/color-picker';
import Selector from 'modules/template-editor/ui/components/elements/selector';
import useTextBlockHelpModal from 'modules/template-editor/ui/hooks/modals/text-block-help';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import alignment from 'modules/overlay-canvas/addons/alignment/alignment';
import history from 'modules/overlay-canvas/addons/history';
import SnapToGrid from 'modules/overlay-canvas/addons/snap-to-grid';
import UDTippy from 'ud-ui/components/tippy';
import {
  ALL_KIT_TYPES,
  AUTO_KIT_TYPE,
  KIT_ELEMENTS_LABELS,
  KIT_ELEMENTS_SETTINGS,
  KIT_TYPE_LABELS,
  KitElementsSetting,
  KitTypeSetting,
} from 'shared/kit';
import { nearestWeight, normalizeFontWeight, primaryFontFamily } from 'shared/fonts';
import { round } from 'shared/utils/round';
import ChoosingPlayer from './components/choosing-player';
import ChoosingReverse from './components/choosing-reverse';
import ChoosingTeam from './components/choosing-team';
import LayerControls from './components/layer-controls';
import FontFamilySelect from './shared/font-family-select';
import FontWeightSelect from './shared/font-weight-select';
import { redrawAfterFonts } from './shared/redraw-after-fonts';
import TransformSection from './shared/transform-section';
import { subscribeLiveTransform } from './shared/use-live-transform';
import LockPosition from './components/lock-position';
import UDEmptyButton from 'modules/ud-ui/components/empty-button';
import { Item, LongDivider, ShortDivider, SubItem } from './styles';
import { useEditorManager } from 'modules/template-editor/ui/context';

interface IProps {
  group: Group;
}

function Placeholder(props: IProps) {
  const { group: placeholder } = props;
  const fabricManager = useEditorManager();
  const { t } = useLingui();
  const kitTypeOptions = useMemo(() => buildKitTypeOptions(t), [t]);
  const kitElementsOptions = useMemo(() => buildKitElementsOptions(t), [t]);

  const { width, height, top, left } = placeholder.getBoundingRect(true) ?? defaultBounds;
  const type: string = placeholder.data?.type ?? 'img';
  const { modal: textBlockHelpModal, open: openTextBlockHelp } = useTextBlockHelpModal();
  const hasTeam = useMemo(() => {
    const lastChar = placeholder.data?.name?.slice(-1);
    const lastCharIsNum = /^\d+$/.test(lastChar);
    return placeholder.data?.hasTeam || lastCharIsNum || placeholder.data?.teamIndex !== undefined;
  }, [placeholder.data?.hasTeam, placeholder.data?.name, placeholder.data?.teamIndex]);
  const lockMovementX = placeholder?.lockMovementX;

  const [teamIndex, setTeamIndex] = useState<0 | 1>(+(placeholder.data?.name?.at(-1) ?? placeholder.data?.teamIndex) as 0 | 1);
  const [playerIndex, setPlayerIndex] = useState<undefined | 0 | 1>(placeholder?.data?.index ?? placeholder.data?.playerIndex);
  const [reverse, setReverse] = useState<undefined | boolean>(placeholder?.data?.reverse ?? placeholder.data?.teamReverse);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: left, y: top });
  const [size, setSize] = useState<{
    width: number;
    height: number;
  }>({ width: round(width), height: round(height) });
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);

  const isTeamKit = placeholder.data?.indicator === 'teamKit';
  const [kitType, setKitType] = useState<KitTypeSetting>(placeholder.data?.kitType ?? AUTO_KIT_TYPE);
  const [kitElements, setKitElements] = useState<KitElementsSetting>(placeholder.data?.kitElements ?? 'full');

  const [fontColor, setFontColor] = React.useState(placeholder?.data?.color);
  const [fontWeight, setFontWeight] = React.useState(placeholder?.data?.fontWeight);
  const [fontFamily, setFontFamily] = React.useState(placeholder?.data?.fontFamily);

  useEffect(() => {
    setPosition({ x: round(left), y: round(top) });
    setSize({ width: round(width), height: round(height) });
    setFontColor(placeholder?.data?.color);
    setFontWeight(placeholder?.data?.fontWeight);
    setFontFamily(placeholder?.data?.fontFamily);
    setTeamIndex(+(placeholder.data?.name?.at(-1) ?? placeholder.data?.teamIndex) as 0 | 1);
    setPlayerIndex((placeholder?.data?.index ?? placeholder.data?.playerIndex) as undefined | 0 | 1);
    setReverse((placeholder?.data?.reverse ?? placeholder.data?.teamReverse) as undefined | boolean);
    setKitType((placeholder.data?.kitType ?? AUTO_KIT_TYPE) as KitTypeSetting);
    setKitElements((placeholder.data?.kitElements ?? 'full') as KitElementsSetting);
    setIsPositionLock(lockMovementX ?? false);

    let updatePosition = () => {
      const x = placeholder?.get('left') ?? 0;
      const y = placeholder?.get('top') ?? 0;
      setPosition({ x: round(x), y: round(y) });
    };

    let updateSize = () => {
      const { width, height } = placeholder?.getBoundingRect(true) ?? defaultBounds;
      setSize({ width: round(width), height: round(height) });
    };

    const offPosition = subscribeLiveTransform(fabricManager?.canvas, updatePosition);
    const offSize = subscribeLiveTransform(fabricManager?.canvas, updateSize);

    return () => {
      offPosition();
      offSize();
    };
    // eslint-disable-next-line
  }, [placeholder]);

  const onChangePosition = (x: number, y: number) => {
    placeholder.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
    setPosition({ x: round(x), y: round(y) });
    fabricManager?.render();
  };

  const onChangeSize = (width: number, height: number) => {
    placeholder.set({ scaleX: width / (placeholder?.width ?? 1), scaleY: height / (placeholder?.height ?? 1) });
    setSize({ width: round(width), height: round(height) });
    SnapToGrid.transformAfterScaling(placeholder);
    fabricManager?.render();
  };

  const onChangeFontColor = (color: string) => {
    setFontColor(color);
    const text = placeholder.getObjects()[0] as Textbox;
    if (!text) return;
    if (placeholder.data?.color === undefined) return;
    placeholder.data = { ...placeholder.data, color };
    text.set('fill', color);
    fabricManager?.render();
    history.saveState();
  };

  const onChangeFontFamily = (value: string) => {
    setFontFamily(value);
    const text = placeholder.getObjects()[0] as Textbox;
    if (!text) return;
    // Плейсхолдер без data.fontFamily (создан до того, как его ставил createGroup) раньше молча не менялся.
    placeholder.data = { ...placeholder.data, fontFamily: value };
    text.set('fontFamily', value);
    // Веса, которого у нового шрифта нет, — ближайший настоящий, иначе браузер синтезирует жирность.
    const family = primaryFontFamily(value);
    const weight = nearestWeight(family, fontWeight);
    if (weight !== normalizeFontWeight(fontWeight, family)) {
      setFontWeight(weight);
      placeholder.data = { ...placeholder.data, fontWeight: weight };
      text.set('fontWeight', weight);
    }
    SnapToGrid.transformAfterScaling(placeholder);
    fabricManager?.render();
    history.saveState();
    void redrawAfterFonts([placeholder], () => {
      SnapToGrid.transformAfterScaling(placeholder);
      fabricManager?.render();
    });
  };
  const onChangeFontWeight = (value: number) => {
    setFontWeight(value);
    const text = placeholder.getObjects()[0] as Textbox;
    if (!text) return;
    placeholder.data = { ...placeholder.data, fontWeight: value };
    text.set('fontWeight', value);
    SnapToGrid.transformAfterScaling(placeholder);
    fabricManager?.render();
    history.saveState();
  };

  const onFocusField = () => {
    fabricManager?.render();
    history.saveState();
  };

  const onChangeTeam = useCallback(
    (teamIndex: 0 | 1) => {
      const oldTeamIndex = placeholder.data?.teamIndex as 0 | 1 | undefined;
      if (oldTeamIndex !== undefined) {
        placeholder.data = { ...placeholder.data, teamIndex };
      } else {
        const name = placeholder.data?.name;
        placeholder.data = { ...placeholder.data, name: name.slice(0, -1) + teamIndex };
        const type = placeholder.data?.type;
        let text: Textbox | undefined;
        switch (type) {
          case 'text': {
            text = placeholder.getObjects()[0] as Textbox | undefined;
            break;
          }
          case 'image': {
            text = placeholder.getObjects()[1] as Textbox | undefined;
            break;
          }
        }

        if (text?.text) {
          const textName = text.text.slice(0, -1) + (teamIndex + 1);
          text?.set({ text: textName });
        }
      }

      fabricManager?.render();
      history.saveState();
      setTeamIndex(teamIndex);
    },
    [fabricManager, placeholder],
  );

  const onChangePlayer = useCallback(
    (index: 0 | 1) => {
      const oldPlayerIndex = placeholder.data?.playerIndex as 0 | 1 | undefined;

      if (oldPlayerIndex !== undefined) {
        placeholder.data = { ...placeholder.data, playerIndex: index };
      } else {
        placeholder.data = { ...placeholder.data, index: index };
      }

      setPlayerIndex(index);
      fabricManager?.render();
      history.saveState();
    },
    [fabricManager, placeholder],
  );

  const onChangeReverse = useCallback(
    (isReverse: boolean) => {
      const oldTeamReverse = placeholder.data?.teamReverse;

      if (oldTeamReverse !== undefined) {
        placeholder.data = { ...placeholder.data, teamReverse: isReverse };
      } else {
        placeholder.data = { ...placeholder.data, reverse: isReverse };
      }

      setReverse(isReverse);
      fabricManager?.render();
      history.saveState();
    },
    [fabricManager, placeholder],
  );

  const onChangeKitType = useCallback(
    (value: KitTypeSetting) => {
      placeholder.data = { ...placeholder.data, kitType: value };
      setKitType(value);
      fabricManager?.render();
      history.saveState();
    },
    [fabricManager, placeholder],
  );

  const onChangeKitElements = useCallback(
    (value: KitElementsSetting) => {
      placeholder.data = { ...placeholder.data, kitElements: value };
      setKitElements(value);
      fabricManager?.render();
      history.saveState();
    },
    [fabricManager, placeholder],
  );

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(placeholder);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(placeholder);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const isDev = process.env.NODE_ENV === 'development';
  const showDebugInfo = true;
  return (
    <>
      <Item>
        <div>{t`Плейсхолдер`}</div>
      </Item>
      <ShortDivider />
      <TransformSection
        position={position}
        size={size}
        onChangePosition={onChangePosition}
        onChangeSize={onChangeSize}
        onAlignCenterHorizontal={onClickAlignmentCenterHorizontal}
        onAlignCenterVertical={onClickAlignmentCenterVertical}
        onFocusField={onFocusField}
      />
      {hasTeam && (
        <>
          <LongDivider />
          <Item>
            <ChoosingTeam onChange={onChangeTeam} value={teamIndex} />
          </Item>
        </>
      )}
      {isTeamKit && (
        <>
          <LongDivider />
          <Item>
            <span>{t`Комплект`}</span>
            <Selector
              onFocus={onFocusField}
              onChange={(event) => {
                onChangeKitType(event?.value as KitTypeSetting);
              }}
              options={kitTypeOptions}
              value={kitTypeOptions.find((option) => option.value === kitType)}
            />
          </Item>
          <Item>
            <span>{t`Элементы`}</span>
            <Selector
              onFocus={onFocusField}
              onChange={(event) => {
                onChangeKitElements(event?.value as KitElementsSetting);
              }}
              options={kitElementsOptions}
              value={kitElementsOptions.find((option) => option.value === kitElements)}
            />
          </Item>
        </>
      )}
      {(playerIndex !== undefined || reverse !== undefined) && (
        <>
          <LongDivider />
          {playerIndex !== undefined && (
            <Item>
              <ChoosingPlayer onChange={onChangePlayer} value={playerIndex} />
            </Item>
          )}
          {reverse !== undefined && (
            <Item>
              <ChoosingReverse onChange={onChangeReverse} value={reverse} />
            </Item>
          )}
        </>
      )}
      {type === 'text' && (
        <>
          <LongDivider />
          <Item style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t`Текстовые блоки`}</span>
            <UDTippy content={t`Пояснение`} placement={'top'}>
              <UDEmptyButton onClick={openTextBlockHelp} icon={<CircleHelp size={16} />} />
            </UDTippy>
          </Item>
          <LongDivider />
          <Item>
            <span>{t`Тип шрифта`}</span>
            <FontFamilySelect value={fontFamily} fontWeight={fontWeight} onChange={onChangeFontFamily} />
            <SubItem style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <FontWeightSelect className={'w-full'} fontFamily={fontFamily} value={fontWeight} onChange={onChangeFontWeight} />
            </SubItem>
          </Item>
          <LongDivider />
          <Item>
            <span>{t`Цвет шрифта`}</span>
            <ColorPicker color={fontColor?.toUpperCase() ?? '#000000'} onChange={onChangeFontColor} />
          </Item>
        </>
      )}
      <LongDivider />
      <LayerControls object={placeholder} />
      <LockPosition objects={[placeholder]} onChange={setIsPositionLock} value={isPositionLock} />
      {isDev && showDebugInfo && (
        <Item>
          scale: <span style={{ whiteSpace: 'pre' }}>{JSON.stringify({ ...placeholder.getObjectScaling() }, null, 2)}</span>
          <br />
          data: <span style={{ whiteSpace: 'pre' }}>{JSON.stringify(placeholder.data, null, 2)}</span>
          <br />
          {type === 'text' && (
            <>
              text: <span style={{ whiteSpace: 'pre' }}>{JSON.stringify(placeholder.getObjects()[0], null, 2)}</span>
            </>
          )}
        </Item>
      )}
      {textBlockHelpModal}
    </>
  );
}

export default Placeholder;

const defaultBounds = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};

/** Подписи переводятся в рендере: `t` приходит из компонента, на уровне модуля язык ещё не известен. */
const buildKitTypeOptions = (t: (descriptor: MessageDescriptor) => string) => [
  { label: t(msg`Авто (по стороне)`), value: AUTO_KIT_TYPE as KitTypeSetting },
  ...ALL_KIT_TYPES.map((type) => ({ label: t(KIT_TYPE_LABELS[type]), value: type as KitTypeSetting })),
];

const buildKitElementsOptions = (t: (descriptor: MessageDescriptor) => string) =>
  KIT_ELEMENTS_SETTINGS.map((setting) => ({
    label: t(KIT_ELEMENTS_LABELS[setting]),
    value: setting,
  }));
